import React, {useMemo, useState} from 'react';
import styled from 'styled-components';
import {Restaurant, PAYMENT_METHOD_NAMES, PAYMENT_METHOD_DESCRIPTIONS, CatalogPromotionDto} from '../types';
import restaurantConfig from '../config/restaurantConfig';
import {
  ModalHeader,
  ModalTitle,
  CloseButton,
  ModalBody
} from './common/modal/ModalComponents';
import Modal from './common/modal/Modal';
import {collectVisiblePromotions, getPromotionBenefitSummary, getPromotionConditionSummary, getPromotionDateRange, getPromotionSummary, getPromotionTypeLabel} from '../utils/promotionUtils';

const TabContainer = styled.div`
  display: flex;
  border-bottom: 1px solid #edf2f7;
  background-color: #f7f9fc;
`;

const Tab = styled.button<{ active: boolean; activeColor: string }>`
  flex: 1;
  padding: 1rem;
  border: none;
  background: none;
  font-weight: 600;
  font-size: 0.9rem;
  color: ${props => props.active ? props.activeColor : '#718096'};
  border-bottom: 3px solid ${props => props.active ? props.activeColor : 'transparent'};
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    color: ${props => props.activeColor};
    background-color: #edf2f7;
  }
`;

const ModalContent = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
`;

const InfoSection = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h3`
  font-size: 1.1rem;
  color: #2d3748;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const InfoText = styled.p`
  color: #4a5568;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0;
`;

const HoursRow = styled.div`
  display: flex;
  justify-content: space-between;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f7f9fc;
  font-size: 0.95rem;

  &:last-child {
    border-bottom: none;
  }
`;

const DayName = styled.span`
  font-weight: 600;
  color: #4a5568;
`;

const TimeRange = styled.span`
  color: #718096;
`;

const PaymentGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(50%, 1fr));
  gap: 0.75rem;
`;

const RatingSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  background-color: #f7f9fc;
  padding: 1.25rem;
  border-radius: 12px;
  margin-bottom: 1.5rem;
`;

const RatingBigValue = styled.div`
  font-size: 2.5rem;
  font-weight: 800;
  color: #2d3748;
`;

const RatingStars = styled.div`
  color: #fbbf24;
  font-size: 1.2rem;
  margin-bottom: 0.25rem;
`;

const RatingDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const ReviewCount = styled.div`
  color: #718096;
  font-size: 0.9rem;
`;

const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1.5rem;
`;

const DetailCard = styled.div<{ $wide?: boolean }>`
  background-color: #f7f9fc;
  border: 1px solid #edf2f7;
  border-radius: 14px;
  padding: 0.9rem 1rem;
  grid-column: ${props => props.$wide ? '1 / -1' : 'auto'};
`;

const DetailLabel = styled.div`
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #718096;
  font-weight: 700;
  margin-bottom: 0.35rem;
`;

const DetailValue = styled.div`
  color: #1f2937;
  font-weight: 700;
  font-size: 0.94rem;
  line-height: 1.4;
  white-space: pre-line;
`;

const PromoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 0.9rem;
`;

const PromoCard = styled.div<{ accent: string }>`
  border-radius: 16px;
  padding: 1rem;
  border: 1px solid ${props => `${props.accent}22`};
  background: linear-gradient(135deg, ${props => `${props.accent}10`} 0%, #ffffff 60%);
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.06);
`;

const PromoName = styled.h4`
  margin: 0 0 0.5rem;
  font-size: 1rem;
  font-weight: 800;
  color: #111827;
`;

const PromoMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  color: #4b5563;
  font-size: 0.84rem;
`;

const PaymentItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background-color: #f7f9fc;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #4a5568;
  font-weight: 500;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1rem;
`;

const SocialLink = styled.a<{ color: string }>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f7f9fc;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #718096;
  font-size: 1.2rem;
  transition: all 0.2s;
  text-decoration: none;

  &:hover {
    background-color: ${props => props.color};
    color: white;
    transform: translateY(-2px);
  }
`;

interface RestaurantInfoModalProps {
  restaurant: Restaurant;
  isOpen: boolean;
  onClose: () => void;
}

const RestaurantInfoModal: React.FC<RestaurantInfoModalProps> = ({ restaurant, isOpen, onClose }) => {
  const [activeTab, setActiveTab] = useState<'about' | 'hours' | 'payment' | 'promotions'>('about');
  const primaryColor = restaurant?.theme?.primary || restaurantConfig.theme.primary;
  const visiblePromotions = useMemo(
    () => collectVisiblePromotions((restaurant.categories || []) as any),
    [restaurant.categories]
  );

  if (!isOpen) return null;

  const getDayName = (day: number) => {
    const days = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    return days[day];
  };

  const renderPaymentIcon = (methodId: string) => {
    const id = methodId.toUpperCase();
    if (id === 'CREDIT_CARD_ONLINE' || id === 'CREDIT_CARD_OFFLINE') return 'fas fa-credit-card';
    if (id === 'DEBIT_CARD_ONLINE' || id === 'DEBIT_CARD_OFFLINE') return 'fas fa-credit-card';
    if (id === 'CASH') return 'fas fa-money-bill-wave';
    if (id === 'PIX_ONLINE' || id === 'PIX_OFFLINE' || id === 'PIX') {
      return (
        <img
          alt=""
          aria-hidden="true"
          src="/pix-logo.svg"
          style={{ width: '20px', height: '20px', objectFit: 'contain', display: 'block' }}
        />
      );
    }
    if (id.includes('MEAL_VOUCHER')) return 'fas fa-utensils';
    if (id === 'GOOGLE_PAY' || id === 'APPLE_PAY') return 'fab fa-google-pay';
    return 'fas fa-wallet';
  };

  const getFriendlyPaymentName = (method: { id: string, name: string }) => {
    const id = method.id.toUpperCase();
    
    if (PAYMENT_METHOD_NAMES[id]) {
      return (
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <span>{PAYMENT_METHOD_NAMES[id]}</span>
          {PAYMENT_METHOD_DESCRIPTIONS[id] && (
            <span style={{ fontSize: '0.75rem', color: '#718096', fontWeight: 400 }}>
              {PAYMENT_METHOD_DESCRIPTIONS[id]}
            </span>
          )}
        </div>
      );
    }
    
    // Se o nome original já for amigável (não for igual ao ID), mantém
    if (method.name && method.name !== method.id) return method.name;
    
    // Caso contrário, tenta formatar o ID (ex: MEAL_VOUCHER -> Meal Voucher)
    return method.id
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const formatPhoneNumber = (phone?: string) => {
    if (!phone) return 'Não informado';

    const digits = phone.replace(/\D/g, '');
    const normalized = digits.length > 11 && digits.startsWith('55') ? digits.slice(2) : digits;

    if (normalized.length === 11) {
      return normalized.replace(/^(\d{2})(\d{1})(\d{4})(\d{4})$/, '($1) $2 $3-$4');
    }

    if (normalized.length === 10) {
      return normalized.replace(/^(\d{2})(\d{4})(\d{4})$/, '($1) $2-$3');
    }

    return phone;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} noPadding zIndex={2000}>
        <ModalHeader className={'pt-4 px-4 flex justify-between items-center'}>
          <ModalTitle>Informações da Loja</ModalTitle>
          <CloseButton color={primaryColor} onClick={onClose}>&times;</CloseButton>
        </ModalHeader>

        <TabContainer>
          <Tab 
            active={activeTab === 'about'} 
            activeColor={primaryColor}
            onClick={() => setActiveTab('about')}
          >
            SOBRE
          </Tab>
          <Tab 
            active={activeTab === 'hours'} 
            activeColor={primaryColor}
            onClick={() => setActiveTab('hours')}
          >
            HORÁRIOS
          </Tab>
          <Tab 
            active={activeTab === 'payment'} 
            activeColor={primaryColor}
            onClick={() => setActiveTab('payment')}
          >
            PAGAMENTO
          </Tab>
          <Tab 
            active={activeTab === 'promotions'} 
            activeColor={primaryColor}
            onClick={() => setActiveTab('promotions')}
          >
            PROMOÇÕES
          </Tab>
        </TabContainer>

        <ModalBody padding="0">
          <ModalContent>
            {activeTab === 'about' && (
              <>
                {restaurant.rating !== undefined && restaurant.rating > 0 && (
                  <RatingSummary>
                    <RatingBigValue>{restaurant.rating.toFixed(1)}</RatingBigValue>
                    <RatingDetails>
                      <RatingStars>
                        {[...Array(5)].map((_, i) => (
                          <i key={i} className={`${i < Math.floor(restaurant.rating || 0) ? 'fas' : 'far'} fa-star`}></i>
                        ))}
                      </RatingStars>
                      {restaurant.reviewCount !== undefined && (
                        <ReviewCount>{restaurant.reviewCount} {restaurant.reviewCount === 1 ? 'avaliação' : 'avaliações'}</ReviewCount>
                      )}
                    </RatingDetails>
                  </RatingSummary>
                )}

                <InfoSection>
                  <SectionTitle>
                    <i className="fas fa-info-circle" style={{ color: primaryColor }}></i>
                    Sobre nós
                  </SectionTitle>
                  <InfoText 
                    dangerouslySetInnerHTML={{ __html: restaurant.description || 'Nenhuma descrição disponível.' }} 
                  />
                </InfoSection>

                <InfoSection>
                  <SectionTitle>
                    <i className="fas fa-map-marker-alt" style={{ color: primaryColor }}></i>
                    Endereço
                  </SectionTitle>
                  <InfoText>
                    {restaurant.address ? (
                      <>
                        {restaurant.address.street}, {restaurant.address.number}
                        {restaurant.address.complement ? ` - ${restaurant.address.complement}` : ''}
                        <br />
                        {restaurant.address.neighborhood}, {restaurant.address.city} - {restaurant.address.state}
                        <br />
                        CEP: {restaurant.address.zipCode}
                      </>
                    ) : 'Endereço não disponível.'}
                  </InfoText>
                </InfoSection>

                {(restaurant.contact?.phone || restaurant.contact?.socialMedia) && (
                  <InfoSection>
                    <SectionTitle>
                      <i className="fas fa-phone" style={{ color: primaryColor }}></i>
                      Contato
                    </SectionTitle>
                    {restaurant.contact?.phone && <InfoText>{formatPhoneNumber(restaurant.contact.phone)}</InfoText>}
                    
                    {restaurant.contact?.socialMedia && (
                      <SocialLinks>
                        {restaurant.contact.socialMedia.instagram && (
                          <SocialLink 
                            href={restaurant.contact.socialMedia.instagram} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            color="#E1306C"
                          >
                            <i className="fab fa-instagram"></i>
                          </SocialLink>
                        )}
                        {restaurant.contact.socialMedia.facebook && (
                          <SocialLink 
                            href={restaurant.contact.socialMedia.facebook} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            color="#4267B2"
                          >
                            <i className="fab fa-facebook-f"></i>
                          </SocialLink>
                        )}
                        {restaurant.contact.socialMedia.whatsapp && (
                          <SocialLink 
                            href={`https://wa.me/${restaurant.contact.socialMedia.whatsapp.replace(/\D/g, '')}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            color="#25D366"
                          >
                            <i className="fab fa-whatsapp"></i>
                          </SocialLink>
                        )}
                      </SocialLinks>
                    )}
                  </InfoSection>
                )}

                <InfoSection>
                  <SectionTitle>
                    <i className="fas fa-truck" style={{ color: primaryColor }}></i>
                    Entrega e operação
                  </SectionTitle>
                  <DetailGrid>
                    <DetailCard>
                      <DetailLabel>Entrega estimada</DetailLabel>
                      <DetailValue>
                        {restaurant.deliveryInfo
                          ? `${restaurant.deliveryInfo.minTime}-${restaurant.deliveryInfo.maxTime} min`
                          : restaurant.deliveryOptions?.takeawayTime || 'Não informado'}
                      </DetailValue>
                    </DetailCard>
                    <DetailCard>
                      <DetailLabel>Entrega</DetailLabel>
                      <DetailValue>{restaurant.deliveryOptions?.delivery ? 'Disponível' : 'Indisponível'}</DetailValue>
                    </DetailCard>
                    <DetailCard>
                      <DetailLabel>Retirada</DetailLabel>
                      <DetailValue>{restaurant.deliveryOptions?.takeaway ? 'Disponível' : 'Indisponível'}</DetailValue>
                    </DetailCard>
                    <DetailCard>
                      <DetailLabel>Atendimento local</DetailLabel>
                      <DetailValue>{restaurant.deliveryOptions?.dineIn ? 'Disponível' : 'Indisponível'}</DetailValue>
                    </DetailCard>
                    <DetailCard>
                      <DetailLabel>Pedido mínimo</DetailLabel>
                      <DetailValue>
                        {typeof restaurant.deliveryOptions?.minimumOrderValue === 'number'
                          ? new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(restaurant.deliveryOptions.minimumOrderValue)
                          : 'Não informado'}
                      </DetailValue>
                    </DetailCard>
                    <DetailCard>
                      <DetailLabel>Agendamento</DetailLabel>
                      <DetailValue>{restaurant.deliveryOptions?.schedulingEnabled ? 'Habilitado' : 'Desabilitado'}</DetailValue>
                    </DetailCard>
                    <DetailCard $wide>
                      <DetailLabel>Faixa de entrega</DetailLabel>
                      <DetailValue>
                        {restaurant.deliveryOptions?.deliveryRadiusConfig?.length
                          ? [...restaurant.deliveryOptions.deliveryRadiusConfig]
                              .sort((a, b) => Number(a.radiusKm) - Number(b.radiusKm))
                              .map(rule => (
                                `${rule.radiusKm}km • ${new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(rule.fee)} • ${rule.estimatedTime}`
                              ))
                              .join('\n')
                          : 'Não informado'}
                      </DetailValue>
                    </DetailCard>
                  </DetailGrid>
                </InfoSection>
              </>
            )}

            {activeTab === 'hours' && (
              <InfoSection>
                <SectionTitle>
                  <i className="fas fa-clock" style={{ color: primaryColor }}></i>
                  Horários de funcionamento
                </SectionTitle>
                {restaurant.openingHours && restaurant.openingHours.length > 0 ? (
                  [...restaurant.openingHours]
                    .sort((a, b) => (a.dayOfWeek === 0 ? 7 : a.dayOfWeek) - (b.dayOfWeek === 0 ? 7 : b.dayOfWeek))
                    .map((day, index) => (
                      <HoursRow key={index}>
                        <DayName>{getDayName(day.dayOfWeek)}</DayName>
                        <TimeRange>
                          {day.timeSlots && day.timeSlots.length > 0 
                            ? day.timeSlots.map(slot => `${slot.open} - ${slot.close}`).join(', ')
                            : 'Fechado'}
                        </TimeRange>
                      </HoursRow>
                    ))
                ) : (
                  <InfoText>Horários não informados.</InfoText>
                )}
              </InfoSection>
            )}

            {activeTab === 'payment' && (
              <InfoSection>
                <SectionTitle>
                  <i className="fas fa-wallet" style={{ color: primaryColor }}></i>
                  Formas de pagamento
                </SectionTitle>
                <PaymentGrid>
                  {restaurant.paymentMethods && restaurant.paymentMethods.length > 0 ? (
                    restaurant.paymentMethods.map(method => (
                      <PaymentItem key={method.id}>
                        {(() => {
                          const icon = renderPaymentIcon(method.id);

                          return typeof icon === 'string' ? (
                            <i className={icon} style={{ color: primaryColor }}></i>
                          ) : (
                            icon
                          );
                        })()}
                        {getFriendlyPaymentName(method)}
                      </PaymentItem>
                    ))
                  ) : (
                    <InfoText>Nenhuma forma de pagamento informada.</InfoText>
                  )}
                </PaymentGrid>
              </InfoSection>
            )}

            {activeTab === 'promotions' && (
              <InfoSection>
                <SectionTitle>
                  <i className="fas fa-bullhorn" style={{ color: primaryColor }}></i>
                  Promoções ativas
                </SectionTitle>

                {visiblePromotions.length > 0 ? (
                  <PromoGrid>
                    {visiblePromotions.map((promo: CatalogPromotionDto) => (
                      <PromoCard key={promo.id} accent={primaryColor}>
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-2 flex-wrap">
                          <span className="badge rounded-pill" style={{ backgroundColor: `${primaryColor}18`, color: primaryColor }}>
                            {getPromotionTypeLabel(promo.type)}
                          </span>
                          {promo.showInMenu && <span className="small text-muted">Exibida no menu</span>}
                        </div>
                        <PromoName>{promo.name}</PromoName>
                        <p className="small text-muted mb-2">
                          {promo.description || 'Promoção ativa e visível para o cliente.'}
                        </p>
                        <PromoMeta>
                          <span>
                            <i className="fas fa-calendar-alt me-2" style={{ color: primaryColor }} />
                            {getPromotionDateRange(promo) || 'Sem período informado'}
                          </span>
                          <span>
                            <i className="fas fa-receipt me-2" style={{ color: primaryColor }} />
                            {getPromotionSummary(promo)}
                          </span>
                          {getPromotionConditionSummary(promo) && (
                            <span>
                              <i className="fas fa-cart-shopping me-2" style={{ color: primaryColor }} />
                              {getPromotionConditionSummary(promo)}
                            </span>
                          )}
                          {getPromotionBenefitSummary(promo) && (
                            <span>
                              <i className="fas fa-gift me-2" style={{ color: primaryColor }} />
                              {getPromotionBenefitSummary(promo)}
                            </span>
                          )}
                        </PromoMeta>
                      </PromoCard>
                    ))}
                  </PromoGrid>
                ) : (
                  <InfoText>Nenhuma promoção ativa e visível no cardápio no momento.</InfoText>
                )}
              </InfoSection>
            )}
          </ModalContent>
        </ModalBody>
    </Modal>
  );
};

export default RestaurantInfoModal;
